import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SiteService } from "../../../core/services/site.service";
import { Countries } from "../../../core/constants/countries";
import { LogHelper } from "../../../core/helpers/log.helper";
import { AlertService } from "../../../shared/alert/alert.service";
import { TemplateService } from "../../../core/services/template.service";
import { SelectOption } from "../../../core/models/select-option.model";
import { ApiConsumerSimple } from "../../../core/models/api-consumer-simple.model";
import { ApiConsumerService } from "../../../core/services/api-consumer.service";
import { TimezoneSelectComponent } from "../../../shared/timezone-select/timezone-select.component";
import { requiredNoWhitespaceValidator } from "../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-site-add',
  templateUrl: './site-add.component.html'
})
export class SiteAddComponent implements OnInit {
  @ViewChild('timezoneSelect') timezoneSelect: TimezoneSelectComponent;
  @ViewChild('logoFileInput') logoFileInput: ElementRef<HTMLInputElement>;

  form: UntypedFormGroup;
  isFormProcessing = false;
  countryOptions: { value: string, text: string }[] = [];
  apiConsumerOptions: SelectOption[] = [];

  logoPreviewUrl: string | null = null;

  // File held until Save is clicked; upload runs inline with the create request so a cancelled
  // form never leaves an orphan blob behind.
  private pendingLogoFile: File | null = null;

  constructor(private templateService: TemplateService, private apiConsumerService: ApiConsumerService, private readonly activatedRoute: ActivatedRoute,
    private readonly siteService: SiteService, private readonly alertService: AlertService, private readonly router: Router) {
    for (const country of Countries.all()) {
      this.countryOptions.push({ value: country.code, text: country.name });
    }
  }

  ngOnInit() {
    this.templateService.showHeader();

    this.form = new UntypedFormGroup({
      name: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.maxLength(100)]),
      irgSiteId: new UntypedFormControl(''),
      address: new UntypedFormControl(''),
      country: new UntypedFormControl(''),
      siteAddress: new UntypedFormControl(null, Validators.required),
      apiConsumer: new UntypedFormControl(false),
      apiConsumerId: new UntypedFormControl(''),
      timezone: new UntypedFormControl('', Validators.required)
    });

    this.loadAndSetApiConsumerOptions();
  }

  onFormSubmit() {
    if (this.form.valid) {
      this.isFormProcessing = true;

      // Upload first (if a file was picked) so we can send the returned filename in the create
      // request. No file → skip straight to create with a null logo.
      if (this.pendingLogoFile) {
        this.siteService.uploadSiteLogo(this.pendingLogoFile).subscribe({
          next: response => this.submitCreate(response.logoFilename),
          error: error => {
            LogHelper.log(error);
            this.isFormProcessing = false;
            this.alertService.showWarningAlert('Unable to upload logo. Check the file and try again.');
          }
        });
      } else {
        this.submitCreate(null);
      }
    }
  }

  private submitCreate(logoFilename: string | null) {
    this.siteService.createSite(
      this.form.get('name').value,
      this.form.get('address').value,
      this.form.get('country').value,
      this.form.get('irgSiteId').value,
      this.form.get('siteAddress').value,
      this.form.get('apiConsumerId').value,
      this.form.get('timezone').value,
      logoFilename
    ).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Site Added Successfully');
        this.router.navigate(['/site']);
      },
      error: error => {
        LogHelper.log(error);
        this.isFormProcessing = false;
        this.alertService.showWarningAlert('Unable to create site, please try again!');
      }
    });
  }

  /**
   * Retrieves and sets the api consumer options to be used in the api consumer dropdown
   */
  loadAndSetApiConsumerOptions(): void {
    this.apiConsumerService.getAllApiConsumers().subscribe({
      next: (apiConsumers: any) => {
        this.apiConsumerOptions = apiConsumers.map((apiConsumer: ApiConsumerSimple) => {
          const consumerName = apiConsumer.enabled ? apiConsumer.name : `${apiConsumer.name} (Temporarily disabled)`;
          return new SelectOption(apiConsumer.id, consumerName);
        });
      }
    });
  }

  onOpenFileBrowser(event: Event) {
    event.preventDefault();
    this.logoFileInput.nativeElement.click();
  }

  onLogoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.pendingLogoFile = file;
    this.logoPreviewUrl = URL.createObjectURL(file);

    // Reset so re-picking the same file still fires (change) again.
    input.value = '';
  }

  onLogoCleared() {
    this.pendingLogoFile = null;
    this.logoPreviewUrl = null;
  }
}
